import { createContext, useContext, useRef, useState } from 'react';
import { CommandStack } from '../commands/CommandStack';

/**
 * CommandContext - Provides command stack to all components
 */
const CommandContext = createContext(null);

/**
 * CommandProvider - Wraps the app with command stack functionality
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {number} [props.maxHistory=50] - Maximum number of commands to keep in history
 *
 * @example
 * <CommandProvider maxHistory={50}>
 *   <App />
 * </CommandProvider>
 */
export const CommandProvider = ({ children, maxHistory = 50 }) => {
  const commandStackRef = useRef(new CommandStack(maxHistory));

  // Force re-render when undo/redo state changes
  const [, setUpdateTrigger] = useState(0);
  const forceUpdate = () => setUpdateTrigger(prev => prev + 1);

  // Wrap CommandStack methods to trigger re-renders
  const commandStack = {
    execute: (command) => {
      commandStackRef.current.execute(command);
      forceUpdate();
    },
    undo: () => {
      const result = commandStackRef.current.undo();
      forceUpdate();
      return result;
    },
    redo: () => {
      const result = commandStackRef.current.redo();
      forceUpdate();
      return result;
    },
    canUndo: () => commandStackRef.current.canUndo(),
    canRedo: () => commandStackRef.current.canRedo(),
    clear: () => {
      commandStackRef.current.clear();
      forceUpdate();
    },
    getUndoCount: () => commandStackRef.current.getUndoCount(),
    getRedoCount: () => commandStackRef.current.getRedoCount(),
    getUndoDescription: () => commandStackRef.current.getUndoDescription(),
    getRedoDescription: () => commandStackRef.current.getRedoDescription(),
  };

  return (
    <CommandContext.Provider value={{ commandStack }}>
      {children}
    </CommandContext.Provider>
  );
};

/**
 * useCommand - Hook to access command stack
 *
 * @returns {{ commandStack: CommandStack }} Command stack instance
 * @throws {Error} If used outside of CommandProvider
 *
 * @example
 * const { commandStack } = useCommand();
 * commandStack.execute(new CreateObjectCommand(canvas, rect));
 * commandStack.undo();
 */
export const useCommand = () => {
  const context = useContext(CommandContext);
  if (!context) {
    throw new Error('useCommand must be used within a CommandProvider');
  }
  return context;
};
